/**
 * Single-bubble component for the conversation timeline.
 *
 * iter135.11 — extracted from ``ConversationsV2Page.jsx`` and
 * subscribed to the global conv store. The component takes only
 * stable props (``convId``, ``msgId``, ``isMine``, ``onEdit``,
 * ``onRecall``) so React.memo's default shallow compare keeps it
 * inert across unrelated state changes. The bubble's actual data
 * comes from ``useMessage(convId, msgId)`` and the seen-by count
 * from ``useReadsByUser(convId)``.
 *
 * As a result:
 *   - Editing message K only re-renders MessageBubble(K).
 *   - A typing tick or a new message arriving does NOT re-render
 *     existing bubbles.
 *   - Read-receipt updates re-render every bubble in the conv,
 *     but only because seen-by count is now per-bubble derived. We
 *     could push that further with a memoised seen-by per seq, but
 *     reads change at most a few times per minute so it's fine.
 */
import { memo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CircleNotch, DotsThreeVertical, Eye, File as FileIcon,
  PencilSimple, Trash, X as XIcon,
} from "@phosphor-icons/react";
import { useT } from "@/lib/i18n";

import AuthImage from "@/components/AuthImage";
import { useMessage, useReadsByUser } from "@/lib/conv/hooks";
import { parseServerTime } from "@/lib/conv/parseServerTime";

function MessageBubbleImpl({ convId, msgId, currentUserKey, isMine, onEdit, onRecall, isFollowUp }) {
  const m = useMessage(convId, msgId);
  const readsByUser = useReadsByUser(convId);

  // The store may briefly not have a message during deletion races
  // — render nothing rather than crash the list.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const t = useT();
  // Flip the message-options menu UPWARD when the bubble sits
  // within ~140 px of the bottom of the scroll container, so the
  // menu stays visible instead of being clipped under the composer.
  const [menuFlipUp, setMenuFlipUp] = useState(false);
  const triggerRef = useRef(null);

  if (!m) return null;

  const startEdit = () => {
    setDraft(m.content || "");
    setEditing(true);
    setMenuOpen(false);
  };
  const submitEdit = () => {
    setEditing(false);
    if ((draft || "").trim() && draft !== m.content && onEdit) onEdit(draft);
  };
  const toggleMenu = () => {
    if (!menuOpen && triggerRef.current) {
      const trigger = triggerRef.current.getBoundingClientRect();
      const scrollEl = triggerRef.current.closest('[data-testid="conv-messages"]');
      const containerBottom = scrollEl
        ? scrollEl.getBoundingClientRect().bottom
        : window.innerHeight;
      setMenuFlipUp(containerBottom - trigger.bottom < 140);
    }
    setMenuOpen((v) => !v);
  };

  // ── Seen-by count for this bubble (derived from the conv's reads
  //    map). Counts every PCS member whose lastSeq >= this msg's
  //    sequence_number, EXCLUDING the sender themselves.
  let seenByCount = 0;
  if (isMine && !m.recalled && readsByUser && readsByUser.size > 0) {
    for (const [k, seq] of readsByUser) {
      if (k === currentUserKey) continue;
      if (seq >= m.sequence_number) seenByCount += 1;
    }
  }

  // iter136 — amber-glow flag when the current viewer is the
  // direct target (or covered by @everyone/@here) of this message.
  const youAreMentioned = (() => {
    if (isMine || m.recalled) return false;
    if (!Array.isArray(m.mentions) || m.mentions.length === 0) return false;
    return m.mentions.some((mn) => {
      if (!mn) return false;
      if (mn.kind === "everyone" || mn.kind === "here") return true;
      if (mn.kind === "user") {
        return `${mn.user_type}:${mn.user_id}` === currentUserKey;
      }
      return false;
    });
  })();

  const renderBody = () => {
    if (m.recalled) return <em className="text-slate-400">🚫 This message was recalled</em>;
    if (m.content_type === "image") {
      return (
        <a href={m.content} target="_blank" rel="noreferrer" data-testid={`conv-msg-image-link-${m.sequence_number}`}>
          <AuthImage
            src={m.content}
            alt="attachment"
            testId={`conv-msg-image-${m.sequence_number}`}
            className="max-w-[280px] max-h-[280px] rounded-md object-cover"
          />
        </a>
      );
    }
    if (m.content_type === "file") {
      const fname = (m.content || "").split("/").pop() || "file";
      return (
        <a
          href={m.content} target="_blank" rel="noreferrer"
          data-testid={`conv-msg-file-${m.sequence_number}`}
          className="inline-flex items-center gap-2 px-2.5 py-2 rounded-md bg-slate-50 border border-slate-200 text-slate-900 text-[13px] hover:bg-slate-100"
        >
          <FileIcon size={16} weight="duotone" className="text-slate-500" />
          <span className="truncate max-w-[200px]">{fname.slice(0, 32)}</span>
        </a>
      );
    }
    if (editing) {
      return (
        <div className="space-y-1.5" data-testid={`conv-msg-edit-form-${m.sequence_number}`}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitEdit(); }
              if (e.key === "Escape") { setEditing(false); }
            }}
            rows={2}
            autoFocus
            className="w-full text-[14.5px] bg-white text-slate-900 border border-emerald-300 rounded-md px-2 py-1 focus:outline-none focus:border-emerald-500"
            data-testid={`conv-msg-edit-textarea-${m.sequence_number}`}
          />
          <div className="flex items-center gap-1.5">
            <button
              onClick={submitEdit}
              className="px-3 py-1 rounded-full text-white text-[10px] font-bold uppercase tracking-wider"
              style={{ background: "#00a884" }}
              data-testid={`conv-msg-edit-save-${m.sequence_number}`}
            >{t("convv2.action_save")}</button>
            <button
              onClick={() => setEditing(false)}
              className="px-3 py-1 rounded-full border border-slate-300 text-slate-600 text-[10px] font-bold uppercase tracking-wider"
            >{t("convv2.action_cancel")}</button>
          </div>
        </div>
      );
    }
    return <span className="whitespace-pre-wrap break-words">{renderContentWithMentions(m.content, currentUserKey)}</span>;
  };

  const ts = m.server_timestamp ? formatBubbleTime(parseServerTime(m.server_timestamp)) : "";
  const showMeta = !editing && m.content_type !== "image" && m.content_type !== "file";
  const metaWidthCols = (() => {
    let w = 0;
    for (const c of ts || "") w += /[\u4e00-\u9fff]/.test(c) ? 2 : 1;
    if (m.edited && !m.recalled) w += 8;
    if (isMine && !m.recalled && seenByCount > 0) w += 5;
    return w;
  })();
  // iter136.1 — split sender display into name vs. company so each
  // can carry its own typographic treatment (see senderLabel JSX
  // below). Falls back to user_type:user_id for unresolved senders.
  const senderName = (m.sender?.name || "").trim();
  const senderCompany = (m.sender?.company_name || "").trim();
  const fallbackLabel = senderName
    ? ""
    : (senderCompany || `${m.sender?.user_type}:${m.sender?.user_id}`);

  return (
    <div
      data-testid={`conv-msg-${m.sequence_number}`}
      className={`flex group ${isMine ? "justify-end" : "justify-start"} items-end gap-1.5`}
    >
      {/* iter136.1 — avatar on the left of the FIRST bubble in a
          follow-up run, only for incoming messages. Matches WhatsApp's
          layout: one avatar per author run rather than one per bubble. */}
      {!isMine && (
        isFollowUp ? (
          <span aria-hidden="true" className="shrink-0 w-7" />
        ) : (
          <span
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10.5px] font-bold text-white shadow-sm"
            style={{ background: bubbleAvatarColor(senderName || senderCompany || "?") }}
            data-testid={`conv-msg-avatar-${m.sequence_number}`}
            title={senderName || senderCompany}
          >
            {bubbleInitials(senderName || senderCompany)}
          </span>
        )
      )}
      <div
        className={`relative max-w-[78%] sm:max-w-[65%] rounded-lg px-2.5 pb-1 shadow-sm ${
          isFollowUp ? "pt-0.5" : "pt-1.5"
        }`}
        data-mentioned={youAreMentioned ? "true" : undefined}
        style={{
          background: m.recalled
            ? "#f1f3f5"
            : (isMine ? "#d9fdd3" : (youAreMentioned ? "#fffbeb" : "#ffffff")),
          // iter135.22 — Slack-style follow-up grouping: when this
          // bubble is a continuation of the previous one (same
          // sender, ≤60 s apart) we square the corner that points
          // toward the predecessor so the run reads as one block.
          borderTopRightRadius: isMine ? (isFollowUp ? 8 : 2) : 8,
          borderTopLeftRadius: isMine ? 8 : (isFollowUp ? 8 : 2),
          // iter136 — amber outline when this message targets the
          // current viewer (direct mention OR @everyone / @here).
          boxShadow: youAreMentioned ? "0 0 0 1.5px #f59e0b" : undefined,
        }}
      >
        {!isMine && !isFollowUp && (
          <div className="mb-0.5 leading-tight truncate flex items-baseline gap-1.5">
            {senderName ? (
              <span
                className="text-[12px] font-bold truncate"
                style={{ color: "#06875b" }}
              >
                {senderName}
              </span>
            ) : null}
            {senderCompany && senderName ? (
              <span
                className="text-[10.5px] truncate font-medium italic font-['Chivo']"
                style={{ color: "#94a3b8", letterSpacing: "0.01em" }}
              >
                {senderCompany}
              </span>
            ) : null}
            {fallbackLabel ? (
              <span
                className="text-[11.5px] font-bold truncate"
                style={{ color: "#06875b" }}
              >
                {fallbackLabel}
              </span>
            ) : null}
          </div>
        )}
        <div className="text-[14.5px] leading-snug text-slate-900 break-words">
          {renderBody()}
          {showMeta && metaWidthCols > 0 && (
            <span
              aria-hidden="true"
              className="inline-block align-bottom select-none invisible"
              style={{ width: `${metaWidthCols * 0.6 + 0.5}em`, height: "1em" }}
            />
          )}
        </div>
        {showMeta && (
          <div className="absolute right-2 bottom-1 flex items-center gap-1 text-[10.5px] text-slate-500 pointer-events-none">
            {m.edited && !m.recalled && <span className="italic">edited</span>}
            <span>{ts}</span>
            {m._optimistic && !m._failed && (
              <CircleNotch size={10} weight="bold" className="animate-spin text-slate-400"
                data-testid={`conv-msg-sending-${m.sequence_number}`} aria-label="Sending" />
            )}
            {m._failed && (
              <span className="inline-flex items-center gap-0.5 font-bold text-rose-500"
                data-testid={`conv-msg-failed-${m.sequence_number}`}
                title={t("convv2.bubble_send_failed")}>
                <XIcon size={11} weight="bold" />
              </span>
            )}
            {isMine && !m.recalled && !m._optimistic && !m._failed && seenByCount > 0 && (
              <span className="inline-flex items-center gap-0.5"
                data-testid={`conv-msg-seenby-${m.sequence_number}`}
                style={{ color: "#0ea5e9" }}
                title={`Seen by ${seenByCount} other ${seenByCount === 1 ? "user" : "users"}`}>
                <Eye size={11} weight="bold" />
                {seenByCount}
              </span>
            )}
          </div>
        )}
        {(m.content_type === "image" || m.content_type === "file") && (
          <div className="flex items-center justify-end gap-1 mt-1 text-[10.5px] text-slate-500">
            {m.edited && !m.recalled && <span className="italic">edited</span>}
            <span>{ts}</span>
            {(m._optimistic || m._uploading) && !m._failed && (
              <CircleNotch size={10} weight="bold" className="animate-spin text-slate-400"
                data-testid={`conv-msg-uploading-${m.sequence_number}`}
                aria-label={m._uploading ? "Uploading" : "Sending"} />
            )}
            {m._failed && (
              <span className="inline-flex items-center gap-0.5 font-bold text-rose-500"
                data-testid={`conv-msg-failed-${m.sequence_number}`}
                title={t("convv2.bubble_upload_failed")}>
                <XIcon size={11} weight="bold" />
              </span>
            )}
            {isMine && !m.recalled && !m._optimistic && !m._failed && seenByCount > 0 && (
              <span className="inline-flex items-center gap-0.5"
                data-testid={`conv-msg-seenby-${m.sequence_number}`}
                style={{ color: "#0ea5e9" }}>
                <Eye size={11} weight="bold" />
                {seenByCount}
              </span>
            )}
          </div>
        )}
        {(onEdit || onRecall) && !m.recalled && !editing && (
          <button
            ref={triggerRef}
            onClick={toggleMenu}
            data-testid={`conv-msg-menu-${m.sequence_number}`}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-slate-200 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-slate-500"
            title={t("convv2.bubble_msg_options")}
          >
            <DotsThreeVertical size={12} weight="bold" />
          </button>
        )}
        {menuOpen && (
          <div
            className={`absolute right-0 z-20 bg-white border border-slate-200 rounded-md shadow-lg py-1 min-w-[120px] ${menuFlipUp ? "bottom-5" : "top-5"}`}
            onMouseLeave={() => setMenuOpen(false)}
          >
            {onEdit && m.content_type === "text" && (
              <button
                onClick={startEdit}
                data-testid={`conv-msg-edit-btn-${m.sequence_number}`}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 flex items-center gap-2 text-slate-700"
              >
                <PencilSimple size={11} weight="bold" /> Edit
              </button>
            )}
            {onRecall && (
              <button
                onClick={() => { setMenuOpen(false); onRecall(); }}
                data-testid={`conv-msg-recall-btn-${m.sequence_number}`}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 flex items-center gap-2 text-rose-600"
              >
                <Trash size={11} weight="bold" /> Recall
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export const MessageBubble = memo(MessageBubbleImpl);

// ── Mention-token renderer ────────────────────────────────────────
// Splits a text run into (text, chip) parts. Chip styling differs
// when the chip targets THIS user — it gets the amber self-mention
// pill instead of the default emerald.
const MENTION_TOKEN_RE =
  /@\[([^\]]{1,80})\]\((staff|person|everyone|here):(\*|\d{1,32})\)/g;

function renderContentWithMentions(content, currentUserKey) {
  if (!content) return null;
  const parts = [];
  let lastIdx = 0;
  let i = 0;
  // Reset regex state for each call (regex is module-scoped).
  MENTION_TOKEN_RE.lastIndex = 0;
  let m;
  while ((m = MENTION_TOKEN_RE.exec(content)) !== null) {
    const [full, name, kind, uid] = m;
    if (m.index > lastIdx) {
      parts.push(content.slice(lastIdx, m.index));
    }
    const isSelf = (kind === "staff" || kind === "person")
      ? `${kind}:${uid}` === currentUserKey
      : false;
    const isSpecial = kind === "everyone" || kind === "here";
    parts.push(
      <span
        key={`m-${i}-${m.index}`}
        data-testid={`conv-mention-chip-${kind}-${uid}`}
        className="inline-flex items-baseline px-1.5 py-[1px] rounded mr-[1px] font-semibold text-[13.5px]"
        style={
          isSelf
            ? { background: "#fef3c7", color: "#92400e" }    // amber self-mention
            : isSpecial
              ? { background: "#dbeafe", color: "#1e40af" }  // blue everyone/here
              : { background: "#d1fae5", color: "#065f46" }  // emerald default
        }
        title={isSpecial ? `@${kind}` : `@${name}`}
      >
        @{isSpecial ? kind : name}
      </span>,
    );
    lastIdx = m.index + full.length;
    i += 1;
  }
  if (lastIdx < content.length) {
    parts.push(content.slice(lastIdx));
  }
  return parts;
}

// ── System (event_type !== null) row — separate component for
//    cheap memoisation; system rows never edit / recall.

function SystemMessageRowImpl({ convId, msgId, isStaff }) {
  const m = useMessage(convId, msgId);
  const navigate = useNavigate();
  if (!m) return null;
  // iter136.14 — prefer the human-readable label resolved by the
  // backend (e.g. Company Name) over the raw event_subject (which is
  // typically a CustomerID natural key).
  const subject = m.event_subject_label || m.event_subject;
  // iter136.15 — staff only: when the event references a Customer
  // (company_added, apply_up_request_*, customer_(de)activated), the
  // label is clickable and deep-links to the Customers page with the
  // detail modal opened on that CustomerID.
  const COMPANY_EVENTS = new Set([
    "company_added", "company_removed",
    "apply_up_request_submitted", "apply_up_request_declined",
    "apply_up_request_accepted", "apply_up_request_expired",
    "customer_deactivated", "customer_reactivated",
  ]);
  const isCompanyLink =
    isStaff
    && m.event_subject
    && m.event_subject_label
    && COMPANY_EVENTS.has(m.event_type);
  const onOpenCustomer = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Routes exist per country; we just use `/customers-us` because
    // the deep-link handler (`?focus=<CustomerID>`) opens a stub
    // detail modal even if the row isn't on the current page — the
    // modal's sub-fetches only need the CustomerID.
    navigate(`/customers-us?focus=${encodeURIComponent(m.event_subject)}`);
  };
  return (
    <div
      data-testid={`conv-msg-${m.sequence_number}`}
      className="flex justify-center my-3"
    >
      <span
        className="text-[10px] font-medium text-slate-600 px-2.5 py-1 rounded-md uppercase tracking-wider"
        style={{ background: "rgba(107, 114, 128, 0.15)", backdropFilter: "blur(2px)" }}
      >
        {(m.event_type || "system").replace(/_/g, " ")}
        {subject ? " · " : ""}
        {subject && isCompanyLink ? (
          <button
            type="button"
            onClick={onOpenCustomer}
            className="font-semibold text-emerald-700 hover:text-emerald-800 underline underline-offset-2 decoration-dotted uppercase tracking-wider"
            data-testid={`conv-sys-company-link-${m.event_subject}`}
            title={`Open customer ${m.event_subject}`}
          >
            {subject}
          </button>
        ) : subject ? (
          <span>{subject}</span>
        ) : null}
      </span>
    </div>
  );
}
export const SystemMessageRow = memo(SystemMessageRowImpl);

// iter136.1 — bubble avatar helpers. Stable colour per author seed
// so the same person always lands on the same circle hue.
const BUBBLE_PALETTE = [
  "#0ea5e9", "#6366f1", "#a855f7", "#ec4899", "#f43f5e",
  "#f97316", "#22c55e", "#14b8a6", "#06875b", "#0891b2",
];
function bubbleAvatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i += 1) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return BUBBLE_PALETTE[h % BUBBLE_PALETTE.length];
}
function bubbleInitials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => (p[0] || "").toUpperCase()).join("") || "?";
}

function formatBubbleTime(d) {
  // Force English locale to keep the chat readable regardless of
  // the viewer's OS language. The TIMEZONE is still the viewer's
  // local zone (handled upstream by ``parseServerTime``); only the
  // AM/PM markers + month names are pinned to English so the
  // padding heuristic above stays correct.
  if (!d) return "";
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay) {
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) {
    return "Yesterday " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}
